import { useCallback, useEffect, useRef, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type {
  BusinessEntity,
  HoursReport,
  PayRun,
  PayRunFinalizeResult,
  PayrollEmployee,
} from "@/api/types";

export type PayRunsStatus = "loading" | "ready" | "error";

/** ef-048-2b — a fresh `Idempotency-Key` per submit, so a double-tapped Post button
 *  replays the ORIGINAL response instead of posting twice. Same fallback-for-older-iOS-
 *  Safari shape `outbox.ts`'s own `newClientId` already documents; this key never leaves
 *  the device except as the header itself, so the (much smaller) collision odds on the
 *  fallback path are an acceptable trade against a hard runtime error. */
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Where the pay run for the period on screen actually is — read off the DOM as
 *  `data-state` (rules/ui-breadcrumbs §1), never inferred from which paragraph happens to
 *  be rendered. `"voided"` (ef-030) takes priority over `"posted"`/`"finalized"` — a
 *  voided run is never simultaneously shown as one still in force. `"posted"` (ef-033)
 *  takes priority over `"finalized"` — a posted run's `finalizedAt` stays set forever
 *  (posting computes no new money), so `postedEntryId` is checked first. The database's
 *  own `pay_run_post_and_void_are_exclusive` CHECK makes `"posted"` and `"voided"` mutually
 *  exclusive in practice, so their relative order here never actually matters. */
export type RunState = "none" | "applied" | "finalized" | "posted" | "voided";

export interface PayPeriodWindow {
  /** `YYYY-MM-DD`, inclusive. */
  start: string;
  end: string;
}

export interface UsePayRunsResult {
  status: PayRunsStatus;
  error: string | null;
  /** True while a period-navigation fetch (previous/next/history) is in flight. The
   *  PREVIOUS period's data stays on screen until the new one resolves — see the note on
   *  `goToPeriod` below for why this is not folded into `status`. */
  navigating: boolean;
  entity: BusinessEntity | null;
  employees: PayrollEmployee[];
  period: PayPeriodWindow | null;
  /** Approved-hours report for `period` — what decides the true empty state (a period
   *  with no run AND zero approved hours anywhere in it) independent of whether a run
   *  happens to exist yet. */
  periodReport: HoursReport | null;
  /** The run matching `period` exactly (`periodStart`/`periodEnd`), or null when this
   *  period has never been applied. */
  run: PayRun | null;
  runState: RunState;
  /** Every run on file, newest `periodStart` first — the server's own rolling window
   *  (`GET /runs` with no range), refreshed after every apply/finalize. */
  history: PayRun[];
  /**
   * The most recent LIVE finalize response, valid only while `finalizeResult.id === run.id`
   * — preferred over `run.lines`' own money (ef-035) only because it is the freshest read,
   * never because `run.lines` lacks the figures now: `GET /runs` persists and returns each
   * line's gross/FICA/withholding/net once its run is finalized. See PayRuns.tsx's own
   * note on the fallback order.
   */
  finalizeResult: PayRunFinalizeResult | null;

  goToPreviousPeriod: () => Promise<void>;
  goToNextPeriod: () => Promise<void>;
  /** Jump straight to a specific run's own period — what a History row click does. Shows
   *  the ACTIVE run for that period when one exists (see `pickRunForPeriod`'s own
   *  `@ui-check`) — the clicked run's `id` is honored only as a tie-break among several
   *  voided rows once no active run stands, never used to shadow a still-standing one. */
  showRun: (run: PayRun) => Promise<void>;

  /** Throws on failure (a 409 means this exact period was already finalized elsewhere).
   *  Resolves once `run`/`history` reflect the write. */
  apply: () => Promise<void>;
  /** Throws on failure — a 400 names every hourly employee still missing a rate, a 409
   *  means somebody else finalized this run first. Resolves once `run`/`history`/
   *  `finalizeResult` reflect the write. */
  finalize: () => Promise<void>;
  /** ef-030 — throws on failure: a 400 means an empty (or too-long) reason, a 409 means
   *  the run was never finalized or was already voided elsewhere. Resolves once
   *  `run`/`history` reflect the write. */
  voidRun: (reason: string) => Promise<void>;
  /** ef-033 — throws on failure: a 400 names every payroll-role account this tenant's
   *  chart of accounts is missing (nothing was posted); a 409 means the run was not
   *  finalized, was voided, or was already posted elsewhere — the caller sees that as an
   *  ordinary thrown `ApiError`, same as `finalize`/`voidRun` above. Resolves once
   *  `run`/`history` reflect the write. */
  post: () => Promise<void>;
  /** Throws on failure. Resolves once `employees` reflects the write. */
  updateEmployeePay: (id: string, payBasis: string, hourlyRate: number | null) => Promise<void>;
}

/** `end - start`, in whole days, both parsed as UTC midnight. */
function daysBetween(startIso: string, endIso: string): number {
  const start = new Date(`${startIso}T00:00:00Z`).getTime();
  const end = new Date(`${endIso}T00:00:00Z`).getTime();
  return Math.round((end - start) / 86_400_000);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * @ui-check: the ONE selection rule for "the run of record" for a period, shared by the
 * initial load, Previous/Next, `showRun` and `apply` — never a bare
 * `.find(r => r.periodStart === X && r.periodEnd === Y)` again. Once a period has both a
 * voided run and a newer active one (exactly what voiding + re-applying produces —
 * `pay_run_one_per_entity_period`, 0031, is a PARTIAL unique index over active rows only),
 * an unfiltered `.find()` can return either row depending on row order.
 *
 * Priority: (1) the active run (`voidedAt == null`) — there is at most one, per that same
 * partial index, so this is never itself ambiguous; (2) `preferId` — a History click's own
 * clicked row, honored only once no active run exists, so a still-standing active run is
 * never shadowed by an older voided row the user happened to click; (3) the most recently
 * voided run, so a FULLY-voided period still shows its history (Voided badge, Apply
 * re-enabled) instead of reading as "none".
 */
function pickRunForPeriod(
  runs: PayRun[],
  start: string,
  end: string,
  preferId?: string,
): PayRun | null {
  const matches = runs.filter((r) => r.periodStart === start && r.periodEnd === end);
  const active = matches.find((r) => r.voidedAt == null);
  if (active) return active;

  const voided = matches.filter((r) => r.voidedAt != null);
  if (voided.length === 0) return null;

  if (preferId) {
    const preferred = voided.find((r) => r.id === preferId);
    if (preferred) return preferred;
  }

  return voided.reduce((latest, r) =>
    new Date(r.voidedAt!).getTime() > new Date(latest.voidedAt!).getTime() ? r : latest,
  );
}

/**
 * ef-034's data layer: the entity's own schedule, the period on screen, the run (if any)
 * that period has, the roster (for the rate-fixup form), and payroll history.
 *
 * ONE ENTITY, same simplifying assumption `usePayrollRoster`'s "primary" scope and every
 * other payroll screen in this build already makes — `BusinessEntity`'s own remarks: "the
 * demo seeds one entity per tenant". A tenant running two entities would need a switcher
 * this hook does not have; flagged here rather than silently mis-scoped.
 *
 * TWO KINDS OF LOADING, DELIBERATELY KEPT APART. `status` gates the FIRST paint — nothing
 * below it renders until the entity, roster and initial period have all resolved once.
 * `navigating` covers every fetch AFTER that (previous/next period, a History click): the
 * previous period's table stays on screen while the next one loads rather than being
 * blanked and re-drawn, which is both the calmer UI and the one that can never show a
 * `data-state` outside `none | applied | finalized` — PayRuns.tsx's own contract.
 */
export function usePayRuns(): UsePayRunsResult {
  const [status, setStatus] = useState<PayRunsStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [navigating, setNavigating] = useState(false);

  const [entity, setEntity] = useState<BusinessEntity | null>(null);
  const [employees, setEmployees] = useState<PayrollEmployee[]>([]);
  const [period, setPeriod] = useState<PayPeriodWindow | null>(null);
  const [periodReport, setPeriodReport] = useState<HoursReport | null>(null);
  const [run, setRun] = useState<PayRun | null>(null);
  const [history, setHistory] = useState<PayRun[]>([]);
  const [finalizeResult, setFinalizeResult] = useState<PayRunFinalizeResult | null>(null);

  // Guards a stale in-flight period fetch from clobbering a NEWER one that resolved
  // first — the same race `useHoursReport`'s own callers avoid with an AbortController,
  // but this hook has several sibling calls per navigation (report + runs), so a single
  // monotonically increasing token is simpler than juggling two controllers per call.
  const periodRequestId = useRef(0);

  const reloadHistory = useCallback(async (): Promise<void> => {
    try {
      const resp = await demoApi.getPayRuns();
      setHistory(resp.runs);
    } catch {
      // Best-effort. The write that triggered this (apply/finalize) already reports its
      // own failure to the caller; History going briefly stale is the lesser failure, not
      // one worth surfacing a second error banner over.
    }
  }, []);

  const goToPeriod = useCallback(
    async (from: string, to: string, preferId?: string): Promise<void> => {
      const requestId = ++periodRequestId.current;
      setNavigating(true);
      try {
        const report = await demoApi.getApprovedHoursReport({ from, to });
        const runsResp = await demoApi.getPayRuns({ from: report.start, to: report.end });
        if (requestId !== periodRequestId.current) return; // superseded by a newer navigation
        const matched = pickRunForPeriod(runsResp.runs, report.start, report.end, preferId);
        setPeriod({ start: report.start, end: report.end });
        setPeriodReport(report);
        setRun(matched);
        // A stale finalize breakdown belongs to whichever run was on screen when it arrived
        // — carrying it into a different period would attach one employee's real numbers to
        // another period's row.
        setFinalizeResult(null);
        setError(null);
      } catch (err) {
        if (requestId !== periodRequestId.current) return;
        setError(problemMessage(err, "Could not load that pay period."));
      } finally {
        if (requestId === periodRequestId.current) setNavigating(false);
      }
    },
    [],
  );

  useEffect(() => {
    // ABORTED, not just flag-guarded — StrictMode's mount/cleanup/remount cycle used to
    // leave this run's own getEntities/getEmployees/getPayRuns/getApprovedHoursReport
    // calls in flight in the background (a `cancelled` bool only gated the setState
    // calls, never the fetch itself), so the FIRST run's own parameterless "current
    // period" requests kept going and could resolve later than expected — racing a
    // real navigation's own fetch and, in `pay-runs.spec.ts`'s "previous period" test,
    // occasionally winning it. Same fix `useHoursReport.ts` already uses.
    const controller = new AbortController();
    void (async () => {
      setStatus("loading");
      try {
        const [entities, emps, historyResp, report] = await Promise.all([
          demoApi.getEntities(controller.signal),
          demoApi.getEmployees(controller.signal),
          demoApi.getPayRuns(undefined, controller.signal),
          demoApi.getApprovedHoursReport(undefined, controller.signal),
        ]);
        if (controller.signal.aborted) return;
        const runsForPeriod = await demoApi.getPayRuns(
          { from: report.start, to: report.end },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        const matched = pickRunForPeriod(runsForPeriod.runs, report.start, report.end);

        setEntity(entities[0] ?? null);
        setEmployees(emps);
        setHistory(historyResp.runs);
        setPeriod({ start: report.start, end: report.end });
        setPeriodReport(report);
        setRun(matched);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setStatus("error");
        setError(problemMessage(err, "Could not load pay runs."));
      }
    })();
    return () => controller.abort();
  }, []);

  const goToPreviousPeriod = useCallback(async (): Promise<void> => {
    if (!period) return;
    const span = daysBetween(period.start, period.end) + 1;
    await goToPeriod(addDaysIso(period.start, -span), addDaysIso(period.end, -span));
  }, [period, goToPeriod]);

  const goToNextPeriod = useCallback(async (): Promise<void> => {
    if (!period) return;
    const span = daysBetween(period.start, period.end) + 1;
    await goToPeriod(addDaysIso(period.start, span), addDaysIso(period.end, span));
  }, [period, goToPeriod]);

  const showRun = useCallback(
    (target: PayRun): Promise<void> =>
      goToPeriod(target.periodStart, target.periodEnd, target.id),
    [goToPeriod],
  );

  const apply = useCallback(async (): Promise<void> => {
    if (!period) return;
    const resp = await demoApi.applyApprovedHours({ from: period.start, to: period.end });
    const matched =
      pickRunForPeriod(resp.runs, period.start, period.end) ?? resp.runs[0] ?? null;
    setRun(matched);
    setFinalizeResult(null);
    // Re-read the report too: apply can pull in hours approved since this period last
    // loaded, and the report's own `approvedHours` figures should agree with what the run
    // now shows.
    try {
      const report = await demoApi.getApprovedHoursReport({ from: period.start, to: period.end });
      setPeriodReport(report);
    } catch {
      // Non-fatal — the apply itself already succeeded and `run` above is real.
    }
    await reloadHistory();
  }, [period, reloadHistory]);

  const finalize = useCallback(async (): Promise<void> => {
    if (!run) return;
    const result = await demoApi.finalizePayRun(run.id);
    setRun((prev) =>
      prev === null
        ? prev
        : {
            ...prev,
            grossTotal: result.grossTotal,
            employerTaxTotal: result.employerTaxTotal,
            withholdingTotal: result.withholdingTotal,
            netTotal: result.netTotal,
            finalizedAt: result.finalizedAt,
          },
    );
    setFinalizeResult(result);
    await reloadHistory();
  }, [run, reloadHistory]);

  const voidRun = useCallback(
    async (reason: string): Promise<void> => {
      if (!run) return;
      const result = await demoApi.voidPayRun(run.id, { reason });
      setRun((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              voidedAt: result.voidedAt,
              voidReason: result.voidReason,
              // ef-036 — null on a never-posted run's void (nothing to reverse), same
              // convention `postedEntryId` already follows for a pre-this-job response.
              reversalEntryId: result.reversalEntryId ?? null,
            },
      );
      // A stale finalize breakdown belongs to a run that no longer stands as the active
      // one for this period — same reasoning as `apply`'s own reset above.
      setFinalizeResult(null);
      await reloadHistory();
    },
    [run, reloadHistory],
  );

  /** ef-033 — posts a FINALIZED run's already-fixed totals as one balanced ledger entry.
   *  No new money is computed here (same reasoning as `finalize` above never re-deriving
   *  a figure the server already owns) — `result.entryId`/`result.postedAt` are written
   *  straight onto `run` so the screen reads `data-state="posted"` on the very same
   *  render that resolved this call, with no extra round trip. */
  const postingRef = useRef(false);

  const post = useCallback(async (): Promise<void> => {
    // ef-049-0a — synchronous, ref-based in-flight guard: checked and set as one
    // synchronous step, so a SECOND same-tick click short-circuits here before ever
    // minting a new Idempotency-Key or calling the API, regardless of whether React has
    // re-rendered the `disabled` attribute yet. The existing `posting`/`submitting`/
    // `payBusy`/`voidBusy` useState flags stay — they still drive the visible `disabled`
    // attribute; this ref is an ADDITIONAL guard, not a replacement.
    if (!run || postingRef.current) return;
    postingRef.current = true;
    try {
      const result = await demoApi.postPayRun(run.id, newIdempotencyKey());
      setRun((prev) =>
        prev === null
          ? prev
          : { ...prev, postedEntryId: result.entryId, postedAt: result.postedAt },
      );
      await reloadHistory();
    } finally {
      postingRef.current = false;
    }
  }, [run, reloadHistory]);

  const updateEmployeePay = useCallback(
    async (id: string, payBasis: string, hourlyRate: number | null): Promise<void> => {
      const updated = await demoApi.updateEmployeePay(id, { payBasis, hourlyRate });
      setEmployees((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    },
    [],
  );

  const runState: RunState =
    run === null
      ? "none"
      : run.voidedAt != null
        ? "voided"
        : run.postedEntryId != null
          ? "posted"
          : run.finalizedAt !== null
            ? "finalized"
            : "applied";

  return {
    status,
    error,
    navigating,
    entity,
    employees,
    period,
    periodReport,
    run,
    runState,
    history,
    finalizeResult,
    goToPreviousPeriod,
    goToNextPeriod,
    showRun,
    apply,
    finalize,
    voidRun,
    post,
    updateEmployeePay,
  };
}
